import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  IconSearch,
  IconChevronDown,
  IconChevronRight,
  IconHeart,
  IconUser,
  IconShoppingCart,
  IconFile,
  IconCopy,
  IconLogin,
  IconMenu,
  IconX
} from '@tabler/icons-react';
import Logo from '../../assets/images/paqtory.png';
import { logoutUser } from '../../redux/slices/authSlice';
import FeaturedCouponsPromo from './widgets/FeaturedCouponsPromo';;
// Custom hook to detect clicks outside an element
const useClickOutside = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {

        const isCategoriesButton = event.target.closest('button')?.textContent?.includes('All Categories');

        if (ref.current && !ref.current.contains(event.target) && !isCategoriesButton) {
          callback();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
};

export default function UserNavbar() {
  const location = useLocation();
  const [topMenuOpened, setTopMenuOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { categories } = useSelector((state) => state.home);
  const { user, token } = useSelector((state) => state.auth);
  const [temporaryNavItems, setTemporaryNavItems] = useState({
    '/track-order': false,
    '/orders': false
  });

  const dispatch = useDispatch();
  const megaMenuRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const isLoggedIn = !!user || !!token;

  // Close menus when clicking outside
  useClickOutside(megaMenuRef, () => setTopMenuOpened(false));
  useClickOutside(accountDropdownRef, () => setAccountDropdownOpen(false));
  useClickOutside(mobileMenuRef, () => setMobileMenuOpen(false));

  useEffect(() => {
    setTemporaryNavItems({
      '/track-order': false,
      '/orders': false
    });
  }, [location.pathname]);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleAccountDropdown = () => {
    setAccountDropdownOpen(!accountDropdownOpen);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!topMenuOpened) setTopMenuOpened(true);
  }

  const filteredCategories = categories
    .map((category) => ({
      ...category,
      subcategories: category.subcategories.filter((subcategory) =>
        subcategory.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter(
      (category) =>
        category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        category.subcategories.length > 0
    );

  const isActive = (path) => {
    // Handle exact matches
    if (location.pathname === path) return true;

    // Handle dynamic routes
    if (path === '/track-order') {
      return location.pathname.startsWith('/track-order');
    }

    return false;
  };

  // NavLink component with elegant hover and active states
  const NavLink = ({ to, children, icon: Icon, mobile = false, temporary = false }) => {
    const shouldShow = !temporary || isActive(to) || temporaryNavItems[to];

    if (!shouldShow) {
      return null;
    }

    return (
      <div className={`relative ${mobile ? 'w-full' : 'px-2'} group`}>
        <Link
          to={to}
          className={`flex items-center gap-1 transition-all duration-200 ${isActive(to) ? 'font-medium' : ''} ${mobile ? 'py-3 px-4 hover:bg-[#0B0C52]/10 w-full' : ''
            }`}
          onClick={() => mobile && setMobileMenuOpen(false)}
        >
          {Icon && <Icon size={20} />}
          <span className="relative">
            {children}
            <span className="absolute left-0 bottom-0 w-full h-[2px] bg-yellow-400 opacity-0 group-hover:opacity-50 
                         transition-opacity duration-300 transform origin-left scale-x-0 group-hover:scale-x-100 
                         rounded-full shadow-[0_2px_8px_rgba(234,179,8,0.4)]"></span>
          </span>
        </Link>
        {isActive(to) && (
          <div className={`absolute ${mobile ? 'left-4 right-4' : 'left-2 right-2'} bottom-0 h-[3px] bg-yellow-400 rounded-full 
                       shadow-[0_2px_8px_rgba(234,179,8,0.6)]`}></div>
        )}
      </div>
    );
  }

  // Mobile menu component
  const MobileMenu = () => (
    <div ref={mobileMenuRef} className="md:hidden absolute top-full left-0 w-full bg-[#0B0C52] shadow-lg z-30 py-4">
      <div className="flex flex-col space-y-2">
        <NavLink to="/" mobile>Home</NavLink>
        <NavLink to="/product-categories">Categories</NavLink>
        <NavLink to="/faq" mobile>FAQ</NavLink>
        <NavLink to="/aboutus" mobile>About Us</NavLink>
        <NavLink to="/contactus" mobile>Contact Us</NavLink>

        {isLoggedIn ? (
          <>
            <NavLink to="/my-designs" mobile icon={IconFile}>My Designs</NavLink>
            <NavLink to="/carts" mobile icon={IconShoppingCart}>Cart</NavLink>
            <NavLink to="/favorites" mobile icon={IconHeart}>Favorite</NavLink>
            <div className="relative px-4 py-3">
              <div
                className="flex items-center gap-1 cursor-pointer group"
                onClick={toggleAccountDropdown}
              >
                <IconUser size={20} />
                <span className="relative">
                  Account
                  <span className="absolute left-0 bottom-0 w-full h-[2px] bg-yellow-400 opacity-0 group-hover:opacity-50 
                                 transition-opacity duration-300 transform origin-left scale-x-0 group-hover:scale-x-100 
                                 rounded-full shadow-[0_2px_8px_rgba(234,179,8,0.4)]"></span>
                </span>
              </div>

              {accountDropdownOpen && (
                <div className="mt-2 ml-8 bg-[#0B0C52]/90 rounded-lg p-2 space-y-2">
                  <Link
                    to="/track-order"
                    className="flex items-center gap-2 px-3 py-2 hover:bg-[#0B0C52]/70 rounded transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      setAccountDropdownOpen(false);
                      setTemporaryNavItems(prev => ({ ...prev, '/track-order': true }));
                      navigate('/track-order');
                    }}
                  >
                    <IconFile size={18} />
                    <span>Track Order</span>
                  </Link>
                  <Link
                    to="/orders"
                    className="flex items-center gap-2 px-3 py-2 hover:bg-[#0B0C52]/70 rounded transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      setAccountDropdownOpen(false);
                      setTemporaryNavItems(prev => ({ ...prev, '/orders': true }));
                      navigate('/orders');
                    }}
                  >
                    <IconCopy size={18} />
                    <span>Order History</span>
                  </Link>
                  <button
                    className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black py-2 px-3 rounded font-bold hover:bg-yellow-500 transition-colors"
                    onClick={() => {
                      setAccountDropdownOpen(false);
                      dispatch(logoutUser());
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <NavLink to="/login" mobile icon={IconLogin}>Login</NavLink>
            <Link
              to="/signup"
              className="mx-4 my-2 bg-white text-[#0B0C52] px-3 py-2 rounded hover:bg-gray-100 transition-colors text-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              Sign Up
            </Link>
          </>
        )}
      </div>
    </div>
  );

  const MegaMenuDropdown = () => {
    const mainCategories = filteredCategories.slice(0, 5);
    const otherProducts = filteredCategories.slice(5);

    const handleLinkClick = () => {
      setTopMenuOpened(false);
      setMobileMenuOpen(false);
    };

    return (
      <div className="absolute top-full left-0 w-full bg-white shadow-lg py-6 z-20">
        <div className="max-w-screen-xl mx-auto text-black">
          <div className="flex flex-col md:flex-row gap-6 pb-2 px-4">
            {/* Render main categories */}
            {mainCategories.map((category) => (
              <div key={category.id} className="flex-shrink-0 w-full md:w-[200px] flex flex-col h-full">
                <div className="flex-grow">
                  <h3 className="font-bold text-lg mb-3 text-[#00148c] hover:text-[#0033cc] transition-colors">
                    {category.name}
                  </h3>
                  <div className="space-y-2 mb-3">
                    {category.subcategories.map((subcategory) => (
                      <Link
                        key={subcategory.id}
                        to={`/category/${category.id}/detail/${subcategory.id}`}
                        onClick={handleLinkClick}
                        className="block pl-2 py-1 rounded-md hover:bg-[#00148c]/10 hover:text-[#00148c] 
                                   active:bg-[#00148c]/20 active:scale-[98%] active:shadow-inner
                                   transition-all duration-150 ease-out text-gray-700 text-sm"
                      >
                        {subcategory.name}
                      </Link>
                    ))}
                  </div>
                  {category.image && (
                    <div className="mt-4">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-[#00148c]/20"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Other products column */}
            {otherProducts.length > 0 && (
              <div className="flex-shrink-0 w-full md:w-[200px] flex flex-col h-full">
                <div className="flex-grow">
                  <h3 className="font-bold text-lg mb-3 text-[#00148c]">Other products</h3>
                  <div className="space-y-2">
                    {otherProducts.map((category) => (
                      <div key={`other-${category.id}`}>
                        {/* Category header */}
                        <button
                          onClick={() => setExpandedCategory(
                            expandedCategory === category.id ? null : category.id
                          )}
                          className="flex items-center justify-between w-full pl-2 py-1 rounded-md hover:bg-[#00148c]/10 text-left"
                        >
                          <span className="font-medium text-gray-800">{category.name}</span>
                          {expandedCategory === category.id ? (
                            <IconChevronDown size={16} />
                          ) : (
                            <IconChevronRight size={16} />
                          )}
                        </button>

                        {/* Subcategories */}
                        {expandedCategory === category.id && (
                          <div className="pl-4 mt-1 space-y-1">
                            {category.subcategories.map((subcategory) => (
                              <Link
                                key={subcategory.id}
                                to={`/category/${subcategory.id}`}
                                onClick={handleLinkClick}
                                className="block py-1 text-sm rounded-md hover:bg-[#00148c]/10 hover:text-[#00148c] 
                                           active:bg-[#00148c]/20 transition-all"
                              >
                                {subcategory.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {filteredCategories.length === 0 && (
              <div className="text-gray-500 text-sm">No results found.</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <nav className="bg-[#0B0C52] text-white relative flex flex-col">
      {/* TOP ROW */}
      <div className="max-w-screen-xl mx-auto w-full px-4 py-2 flex items-center justify-between">
        {/* Logo and mobile menu button */}
        <div className="flex items-center gap-4">
          <button
            className="md:hidden text-white focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <IconX size={24} /> : <IconMenu size={24} />}
          </button>

          <Link to="/" className="flex items-center">
            <div className="w-[50px] h-[50px] md:w-[60px] md:h-[60px] rounded-full bg-white overflow-hidden flex items-center justify-center">
              <img src={Logo} alt="Logo" className="w-full h-full object-contain" />
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-0 flex-1">
          {/* Main navigation links */}
          <div className="flex items-center ml-4 space-x-1 w-[600px]">
            <NavLink to="/">Home</NavLink>

            <NavLink to="/product-categories">Categories</NavLink>
            <NavLink to="/faq">FAQ</NavLink>
            <NavLink to="/aboutus">About Us</NavLink>
            <NavLink to="/contactus">Contact Us</NavLink>
          </div>
        </div>

        {/* Right side icons - desktop */}
        <div className="hidden md:flex flex-col md:flex-row items-center ml-8 gap-4">
          {isLoggedIn ? (
            <>
              <NavLink to="/my-designs" icon={IconFile}>My Designs</NavLink>
              <NavLink to="/carts" icon={IconShoppingCart}>Cart</NavLink>
              <NavLink to="/favorites" icon={IconHeart}>Favorite</NavLink>
              <NavLink to="/track-order" icon={IconFile} temporary>Track Order</NavLink>
              <NavLink to="/orders" icon={IconCopy} temporary>Order History</NavLink>

              <div className="relative">
                <div
                  className="flex items-center gap-1 cursor-pointer px-2 group"
                  onClick={toggleAccountDropdown}
                >
                  <IconUser size={20} />
                  <span className="relative">
                    Account
                    <span className="absolute left-0 bottom-0 w-full h-[2px] bg-yellow-400 opacity-0 group-hover:opacity-50 
                                   transition-opacity duration-300 transform origin-left scale-x-0 group-hover:scale-x-100 
                                   rounded-full shadow-[0_2px_8px_rgba(234,179,8,0.4)]"></span>
                  </span>
                </div>

                {accountDropdownOpen && (
                  <div ref={accountDropdownRef} className="absolute right-[-100px] mt-4 w-64 z-[15000]">
                    {/* Pointer */}
                    <div className="flex justify-center">
                      <div className="w-4 h-4 bg-white rotate-45 transform translate-y-2 shadow -mb-2 z-0" />
                    </div>

                    <div className="bg-white rounded-md shadow-xl border border-gray-200 overflow-hidden relative z-10">
                      {/* Profile Section */}
                      <div className="flex flex-col items-center p-4">
                        <img
                          src={user?.profile_picture || 'https://via.placeholder.com/150'}
                          alt="Profile"
                          className="w-20 h-20 rounded-full shadow-md object-cover"
                        />
                        <div className="mt-2 font-semibold text-[#00148c] text-center">
                          {user?.full_name || '--'}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="divide-y divide-[#00148c] border-t border-[#00148c] text-black font-medium text-[15px]">
                        <Link
                          to="/track-order"
                          className="flex items-center gap-3 px-4 py-3 hover:bg-[#f0f0f0] transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            setAccountDropdownOpen(false);
                            setTemporaryNavItems(prev => ({ ...prev, '/track-order': true }));
                            navigate('/track-order');
                          }}
                        >
                          <IconFile size={20} stroke={2} className="text-[#00148c]" />
                          <span>Track Order</span>
                        </Link>

                        <Link
                          to="/orders"
                          className="flex items-center gap-3 px-4 py-3 hover:bg-[#f0f0f0] transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            setAccountDropdownOpen(false);
                            setTemporaryNavItems(prev => ({ ...prev, '/orders': true }));
                            navigate('/orders');
                          }}
                        >
                          <IconCopy size={20} stroke={2} className="text-[#00148c]" />
                          <span>Order History</span>
                        </Link>

                        {isLoggedIn && (
                          <button
                            className="flex items-center justify-center bg-yellow-400 text-black py-3 font-bold w-full
                                       hover:bg-yellow-500 transition-colors"
                            onClick={() => {
                              setAccountDropdownOpen(false);
                              dispatch(logoutUser());
                            }}
                          >
                            Logout
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <NavLink to="/login" icon={IconLogin}>Login</NavLink>
              <Link to="/signup" className="bg-white text-[#0B0C52] px-3 py-1 rounded hover:bg-gray-100 transition-colors">
                Sign Up
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && <MobileMenu />}

      {/* BOTTOM ROW - Search and promo */}
      <div className="max-w-screen-xl mx-auto w-full px-4 py-2 flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Search Box - now full width on mobile */}
        <div className="w-full md:w-[600px] ">
          <div className="flex items-center bg-white border border-[#FFF] rounded h-10 overflow-hidden w-full">
            <button
              onClick={() => {

                setTopMenuOpened((prev) => !prev);

              }}
              className="flex items-center gap-1 text-[#00148c] font-medium px-3 text-sm"
            >
              <span>All Categories</span>
              <IconChevronDown
                size={16}
                color="#00148c"
                className={`transition-transform ${topMenuOpened ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="w-px h-full bg-[#0B0C52]" />
            <input
              type="text"
              placeholder="Search for categories or subcategories..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="flex-1 px-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
            />
            <button className="bg-[#0B0C52] text-white px-3 flex items-center justify-center h-full hover:bg-[#0B0C52]/90 transition-colors">
              <IconSearch size={16} />
            </button>
          </div>
        </div>

        {/* Promo Area - Only shown when logged in */}

      <FeaturedCouponsPromo />

      </div>

      {/* Mega menu dropdown */}
      {topMenuOpened && (
        <div ref={megaMenuRef}>
          <MegaMenuDropdown />
        </div>
      )}
    </nav>
  );
}